import { GridRowModes, GridToolbarContainer, useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import EastIcon from '@mui/icons-material/East';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Box, IconButton } from '@mui/material';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, FormErrorUI, AddButtonUi, AutoCompleteSelectUI, DatagridPremiumUi } from '../Interface';
import simpletaskService from '../../service/simpletask.service';
import useUsers from '../../context/UsersContext';
import dayjs from 'dayjs';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { ResourceType } from '../../config/simpleTask';

const EditToolbar = (props) => {
  const { disabled, push, defaultResource, dialog } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultResource.id = uuidv4();
    defaultResource.resourceType = ResourceType.RESOURCETWO;
    await push(defaultResource);

    await validateField(`resources.${values.resources.length ?? 0}.resourceTwoId`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ResourcesTable = ({ disabled, defaultResource, autocompleteOptions, handleLoadValues, dialog }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { user, getUser } = useUsers();

  const getValueIndex = (values, id) => {
    return values.resources.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'resourceTwoId',
        headerName: 'Erőforrás',
        editable: true,
        sortable: false,
        type: 'string',
        renderCell: (data) => {
          const findedResource = autocompleteOptions.resourceTwo.find((item) => item.value === data.row.resourceTwoId);
          return findedResource?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedUser = autocompleteOptions.resourceTwo.find(
            (u) => u.value === values.resources?.[valueIndex]?.resourceTwoId
          );
          return (
            <Field
              name={`resources.${valueIndex}.resourceTwoId`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.resources?.[valueIndex]?.resourceTwoId} />}
              onChange={(_e, newVal) => {
                setFieldValue(`resources.${valueIndex}.resourceTwoId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`resources.${valueIndex}.resourceTwoId`)}
              value={findedUser}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={autocompleteOptions.resourceTwo}
            />
          );
        },
      },

      {
        field: 'next',
        type: 'string',
        headerName: 'Megtekintés',
        renderCell: (params) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              onClick={() => handleLoadValues(params.row)}
              disabled={params.row.isNew}
            >
              <EastIcon />
            </IconButton>
          );
        },
      },

      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);

                if (!row.isNew) {
                  simpletaskService
                    .deleteSimpleTask(row.simpleTaskId)
                    .then(() => {
                      addNotification({
                        content: 'Sikeres törlés!',
                        type: NotificationType.WARNING,
                      });

                      if (dialog) {
                        dialog.setTaskChanged(true);
                      }
                    })
                    .catch(() => {
                      return addNotification({
                        content: 'Sikertelen törlés!',
                        type: NotificationType.ERROR,
                      });
                    });
                }
                remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };
  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="resources">
            {({ push, remove }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ResourcesTable}
                height={380}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.resources?.filter((resource) => resource.resourceType === ResourceType.RESOURCETWO) ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultResource,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                editMode="row"
                onCellClick={(params, event) => {
                  if (disabled || !params.row.isNew) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ResourcesTable;
